import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger
} from '../lib/dataGrid';
import SearchField from './SearchField';
import ActiveToolButton from './ActiveToolButton';
import { QUICK_SEARCH_ATTRIBUTE } from './quickSearchShortcut';

/**
 * Quick-filter search box for the data grids; it filters across all columns.
 * Rendered by GridCrudToolbar, which owns the expanded state because the
 * toolbar has to yield its spacer to a filling field.
 *
 * Collapsed, it is a search icon that expands the field on tap. A dot on the
 * icon marks a filter that is still applied while collapsed.
 */
export default function GridQuickFilter({
    expanded,
    onExpandedChange,
    fills
}: {
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    /** Take the free space rather than the fixed 240px width. */
    fills: boolean;
}) {
    const intl = useIntl();
    const placeholder = intl.formatMessage({ id: 'grid.search' });

    return (
        <QuickFilter
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            render={
                <Box
                    // Found by Strg+Shift+F (see quickSearchShortcut).
                    {...{ [QUICK_SEARCH_ATTRIBUTE]: '' }}
                    sx={{
                        display: 'flex',
                        minWidth: 0,
                        flex: fills ? '1 1 0' : '0 0 auto'
                    }}
                />
            }
        >
            {expanded ? (
                <QuickFilterControl
                    render={({ ref, slotProps, ...controlProps }, state) => (
                        <SearchField
                            {...controlProps}
                            inputRef={ref}
                            slotProps={slotProps}
                            value={state.value}
                            placeholder={placeholder}
                            aria-label={placeholder}
                            sx={{ width: fills ? '100%' : 240 }}
                            endAdornment={
                                state.value ? (
                                    <QuickFilterClear
                                        edge="end"
                                        size="small"
                                        aria-label={intl.formatMessage({
                                            id: 'grid.search.clear'
                                        })}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </QuickFilterClear>
                                ) : null
                            }
                        />
                    )}
                />
            ) : (
                <QuickFilterTrigger
                    render={(triggerProps, state) => (
                        <Tooltip title={placeholder}>
                            <ActiveToolButton
                                {...triggerProps}
                                // Collapsed, the term itself is out of sight —
                                // so the button carries the same filled mark
                                // the Filters button does.
                                active={Boolean(state.value)}
                            >
                                <Badge
                                    badgeContent={state.value ? 1 : 0}
                                    color="info"
                                    variant="dot"
                                >
                                    <SearchIcon fontSize="small" />
                                </Badge>
                            </ActiveToolButton>
                        </Tooltip>
                    )}
                />
            )}
        </QuickFilter>
    );
}
